import React, { useEffect, useState } from "react";
import {
  login,
  forgotPassword,
  confirmForgotPassword,
} from "../apiService";
import { useNavigate } from "react-router-dom";
import "../App.css";

const FeedbackLoginModal = ({
  isOpen,
  onLoginSuccess,
  onClose = () => {},
}) => {
  const navigate = useNavigate();

  const [internalOpen, setInternalOpen] = useState(isOpen);

  const [mode, setMode] = useState("login");

  // Login
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Forgot password
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Confirm password reset
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  // Messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [loading, setLoading] = useState(false);

  // OTP countdown
  const [countdown, setCountdown] = useState(0);

  /*
   * Keep modal synchronized with parent
   */
  useEffect(() => {
    setInternalOpen(isOpen);
  }, [isOpen]);

  /*
   * OTP countdown
   */
  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((previous) => previous - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  /*
   * Safely convert API errors to text
   */
  const formatError = (value) => {
    if (value === null || value === undefined) {
      return "Something went wrong. Please try again.";
    }

    if (typeof value === "string") {
      return value;
    }

    if (
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (
            item &&
            typeof item === "object" &&
            item.msg
          ) {
            return item.msg;
          }

          return formatError(item);
        })
        .filter(Boolean)
        .join(" ");
    }

    /*
     * FastAPI validation error
     */
    if (
      typeof value === "object" &&
      value.msg
    ) {
      return value.msg;
    }

    if (
      typeof value === "object" &&
      value.detail
    ) {
      return formatError(value.detail);
    }

    if (
      typeof value === "object" &&
      value.message
    ) {
      return formatError(value.message);
    }

    if (
      typeof value === "object" &&
      value.error
    ) {
      return formatError(value.error);
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "Something went wrong. Please try again.";
    }
  };

  /*
   * Get readable API error
   */
  const getErrorMessage = (err) => {
    const responseData = err?.response?.data;

    if (!responseData) {
      return (
        err?.message ||
        "Something went wrong. Please try again."
      );
    }

    if (
      responseData.detail !== undefined
    ) {
      return formatError(responseData.detail);
    }

    if (
      responseData.message !== undefined
    ) {
      return formatError(responseData.message);
    }

    return formatError(responseData);
  };

  /*
   * Clear messages
   */
  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  /*
   * Reset modal
   */
  const resetForm = () => {
    setMode("login");

    setUsername("");
    setPassword("");

    setEmail("");
    setPhoneNumber("");

    setOtp("");
    setNewPassword("");
    setConfirmPassword("");

    setShowPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);

    setError("");
    setSuccess("");

    setLoading(false);
    setCountdown(0);
  };

  /*
   * CLOSE MODAL
   */
  const handleClose = () => {
    setInternalOpen(false);

    resetForm();

    if (typeof onClose === "function") {
      onClose();
    }
  };

  /*
   * ========================================================
   * LOGIN
   * ========================================================
   *
   * Accepts:
   * - Email
   * - Mobile number
   *
   * After successful login:
   * - login() saves the token and fires the app-wide auth event,
   *   so the whole site (navbar, header, pages) updates instantly
   * - Close modal
   * - Notify parent
   * - Redirect to Feedback page
   */
  const handleLogin = async (event) => {
    event.preventDefault();

    clearMessages();

    if (!username.trim()) {
      setError(
        "Please enter your email or mobile number."
      );
      return;
    }

    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);

    try {
      /*
       * navigate is intentionally NOT passed to login().
       * Passing it made login() redirect to /profile while this modal
       * redirected to /feedback, and the two redirects raced each other.
       * This modal owns the redirect below.
       */
      const result = await login(
        username.trim(),
        password
      );

      console.log(
        "Feedback login response:",
        result
      );

      /*
       * Check successful login
       */
      if (result?.success) {
        /*
         * Close modal
         */
        setInternalOpen(false);

        /*
         * Tell parent login was successful
         */
        if (
          typeof onLoginSuccess ===
          "function"
        ) {
          onLoginSuccess();
        }

        /*
         * Redirect to Feedback page
         *
         * CHANGE THIS PATH ONLY IF YOUR
         * ROUTER USES A DIFFERENT PATH.
         */
        navigate("/feedback");

        return;
      }

      /*
       * Login failed
       */
      setError(
        formatError(
          result?.detail ||
            result?.message ||
            "Invalid email/mobile number or password."
        )
      );
    } catch (err) {
      console.error(
        "Feedback login error:",
        err
      );

      setError(
        getErrorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ========================================================
   * FORGOT PASSWORD
   * ========================================================
   */
  const handleForgotPassword = async (
    event
  ) => {
    event.preventDefault();

    clearMessages();

    if (!email.trim()) {
      setError(
        "Please enter your registered email."
      );
      return;
    }

    if (!phoneNumber.trim()) {
      setError(
        "Please enter your registered phone number."
      );
      return;
    }

    setLoading(true);

    try {
      const result =
        await forgotPassword(
          email.trim(),
          phoneNumber.trim()
        );

      /*
       * Some API functions may not return
       * { success: true }, so treat a
       * successful promise as success unless
       * the result explicitly says otherwise.
       */
      if (
        result === undefined ||
        result === null ||
        result?.success !== false
      ) {
        setMode("confirm");
        setCountdown(60);

        setSuccess(
          "A password reset code has been sent to you."
        );
      } else {
        setError(
          formatError(
            result?.detail ||
              result?.message ||
              "Unable to send password reset code."
          )
        );
      }
    } catch (err) {
      console.error(
        "Forgot password error:",
        err
      );

      setError(
        getErrorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ========================================================
   * CONFIRM PASSWORD RESET
   * ========================================================
   */
  const handleConfirmReset = async (
    event
  ) => {
    event.preventDefault();

    clearMessages();

    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }

    if (!newPassword) {
      setError(
        "Please enter a new password."
      );
      return;
    }

    if (!confirmPassword) {
      setError(
        "Please confirm your new password."
      );
      return;
    }

    if (
      newPassword !== confirmPassword
    ) {
      setError(
        "New password and confirmation do not match."
      );
      return;
    }

    setLoading(true);

    try {
      const result =
        await confirmForgotPassword({
          email: email.trim(),
          mobileNumber:
            phoneNumber.trim(),
          otp: otp.trim(),
          newPassword,
        });

      if (
        result === undefined ||
        result === null ||
        result?.success !== false
      ) {
        setSuccess(
          "Password reset successfully. You can now log in."
        );

        setTimeout(() => {
          setMode("login");

          setPassword("");
          setOtp("");
          setNewPassword("");
          setConfirmPassword("");

          setSuccess("");
        }, 2500);
      } else {
        setError(
          formatError(
            result?.detail ||
              result?.message ||
              "Unable to reset your password."
          )
        );
      }
    } catch (err) {
      console.error(
        "Password reset error:",
        err
      );

      setError(
        getErrorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ========================================================
   * RESEND OTP
   * ========================================================
   */
  const handleResendOTP = async () => {
    if (
      countdown > 0 ||
      loading
    ) {
      return;
    }

    clearMessages();

    if (!email.trim()) {
      setError(
        "Please enter your email address."
      );
      return;
    }

    if (!phoneNumber.trim()) {
      setError(
        "Please enter your phone number."
      );
      return;
    }

    setLoading(true);

    try {
      const result =
        await forgotPassword(
          email.trim(),
          phoneNumber.trim()
        );

      if (
        result === undefined ||
        result === null ||
        result?.success !== false
      ) {
        setCountdown(60);

        setSuccess(
          "OTP resent successfully."
        );
      } else {
        setError(
          formatError(
            result?.detail ||
              result?.message ||
              "Unable to resend OTP."
          )
        );
      }
    } catch (err) {
      console.error(
        "Resend OTP error:",
        err
      );

      setError(
        getErrorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * BACK TO LOGIN
   */
  const handleBackToLogin = () => {
    clearMessages();
    setMode("login");
  };

  /*
   * Don't render when closed
   */
  if (!internalOpen) {
    return null;
  }

  return (
    <>
      {/* ====================================================
          LOGIN MODAL
      ===================================================== */}
      {mode === "login" && (
        <div
          className="modal show feedback-login-modal"
          style={{
            display: "block",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              {/* HEADER */}
              <div className="modal-header feedback-modal-header">

                <h5 className="modal-title">
                  Login
                </h5>

                <button
                  type="button"
                  className="feedback-close-btn"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  ×
                </button>

              </div>

              {/* BODY */}
              <div className="modal-body">

                <form
                  onSubmit={handleLogin}
                >

                  {/* ERROR */}
                  {error && (
                    <div
                      className="alert alert-danger"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}

                  {/* SUCCESS */}
                  {success && (
                    <div
                      className="alert alert-success"
                      role="alert"
                    >
                      {success}
                    </div>
                  )}

                  {/* USERNAME */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-username"
                      className="form-label"
                    >
                      Email or Phone Number
                    </label>

                    <input
                      id="feedback-username"
                      type="text"
                      className="form-control"
                      value={username}
                      onChange={(e) =>
                        setUsername(
                          e.target.value
                        )
                      }
                      placeholder="Enter email or phone number"
                      autoComplete="username"
                      required
                    />

                  </div>

                  {/* PASSWORD */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-password"
                      className="form-label"
                    >
                      Password
                    </label>

                    <div className="input-group">

                      <input
                        id="feedback-password"
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        className="form-control"
                        value={password}
                        onChange={(e) =>
                          setPassword(
                            e.target.value
                          )
                        }
                        autoComplete="current-password"
                        required
                      />

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setShowPassword(
                            (value) =>
                              !value
                          )
                        }
                      >
                        {showPassword
                          ? "Hide"
                          : "Show"}
                      </button>

                    </div>

                  </div>

                  {/* LOGIN BUTTON */}
                  <button
                    type="submit"
                    className="ftthsa btn-secondary w-100"
                    disabled={loading}
                  >
                    {loading
                      ? "Logging in..."
                      : "Login"}
                  </button>

                  {/* FORGOT PASSWORD */}
                  <div className="text-center mt-3">

                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => {
                        clearMessages();
                        setMode("forgot");
                      }}
                    >
                      Forgot Password?
                    </button>

                  </div>

                </form>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================
          FORGOT PASSWORD MODAL
      ===================================================== */}
      {mode === "forgot" && (
        <div
          className="modal show feedback-login-modal"
          style={{
            display: "block",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              {/* HEADER */}
              <div className="modal-header feedback-modal-header">

                <h5 className="modal-title">
                  Forgot Password
                </h5>

                <button
                  type="button"
                  className="feedback-close-btn"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  ×
                </button>

              </div>

              {/* BODY */}
              <div className="modal-body">

                <form
                  onSubmit={
                    handleForgotPassword
                  }
                >

                  {error && (
                    <div
                      className="alert alert-danger"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}

                  {success && (
                    <div
                      className="alert alert-success"
                      role="alert"
                    >
                      {success}
                    </div>
                  )}

                  {/* EMAIL */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-email"
                      className="form-label"
                    >
                      Enter Registered Email
                    </label>

                    <input
                      id="feedback-email"
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) =>
                        setEmail(
                          e.target.value
                        )
                      }
                      autoComplete="email"
                      required
                    />

                  </div>

                  {/* PHONE */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-phone"
                      className="form-label"
                    >
                      Enter Registered Phone Number
                    </label>

                    <input
                      id="feedback-phone"
                      type="text"
                      className="form-control"
                      value={phoneNumber}
                      onChange={(e) =>
                        setPhoneNumber(
                          e.target.value
                        )
                      }
                      autoComplete="tel"
                      required
                    />

                  </div>

                  {/* SEND CODE */}
                  <button
                    type="submit"
                    className="ftthsa btn-secondary w-100"
                    disabled={loading}
                  >
                    {loading
                      ? "Sending..."
                      : "Submit"}
                  </button>

                  {/* BACK */}
                  <div className="text-center mt-3">

                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={
                        handleBackToLogin
                      }
                    >
                      Back to Login
                    </button>

                  </div>

                </form>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================
          CONFIRM PASSWORD RESET
      ===================================================== */}
      {mode === "confirm" && (
        <div
          className="modal show feedback-login-modal"
          style={{
            display: "block",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              {/* HEADER */}
              <div className="modal-header feedback-modal-header">

                <h5 className="modal-title">
                  Confirm Password Reset
                </h5>

                <button
                  type="button"
                  className="feedback-close-btn"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  ×
                </button>

              </div>

              {/* BODY */}
              <div className="modal-body">

                <form
                  onSubmit={
                    handleConfirmReset
                  }
                >

                  {error && (
                    <div
                      className="alert alert-danger"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}

                  {success && (
                    <div
                      className="alert alert-success"
                      role="alert"
                    >
                      {success}
                    </div>
                  )}

                  {/* OTP */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-otp"
                      className="form-label"
                    >
                      OTP
                    </label>

                    <input
                      id="feedback-otp"
                      type="text"
                      className="form-control"
                      value={otp}
                      onChange={(e) =>
                        setOtp(
                          e.target.value
                        )
                      }
                      autoComplete="one-time-code"
                      required
                    />

                  </div>

                  {/* NEW PASSWORD */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-new-password"
                      className="form-label"
                    >
                      New Password
                    </label>

                    <div className="input-group">

                      <input
                        id="feedback-new-password"
                        type={
                          showNewPassword
                            ? "text"
                            : "password"
                        }
                        className="form-control"
                        value={newPassword}
                        onChange={(e) =>
                          setNewPassword(
                            e.target.value
                          )
                        }
                        autoComplete="new-password"
                        required
                      />

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setShowNewPassword(
                            (value) =>
                              !value
                          )
                        }
                      >
                        {showNewPassword
                          ? "Hide"
                          : "Show"}
                      </button>

                    </div>

                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="mb-3">

                    <label
                      htmlFor="feedback-confirm-password"
                      className="form-label"
                    >
                      Confirm New Password
                    </label>

                    <div className="input-group">

                      <input
                        id="feedback-confirm-password"
                        type={
                          showConfirmPassword
                            ? "text"
                            : "password"
                        }
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) =>
                          setConfirmPassword(
                            e.target.value
                          )
                        }
                        autoComplete="new-password"
                        required
                      />

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setShowConfirmPassword(
                            (value) =>
                              !value
                          )
                        }
                      >
                        {showConfirmPassword
                          ? "Hide"
                          : "Show"}
                      </button>

                    </div>

                  </div>

                  {/* RESET */}
                  <button
                    type="submit"
                    className="ftthsa btn-secondary w-100"
                    disabled={loading}
                  >
                    {loading
                      ? "Resetting..."
                      : "Reset Password"}
                  </button>

                  {/* RESEND OTP */}
                  <button
                    type="button"
                    className="btn btn-link w-100 mt-2"
                    onClick={
                      handleResendOTP
                    }
                    disabled={
                      countdown > 0 ||
                      loading
                    }
                  >
                    {countdown > 0
                      ? `Resend OTP (${countdown}s)`
                      : "Resend OTP"}
                  </button>

                  {/* BACK */}
                  <div className="text-center">

                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={
                        handleBackToLogin
                      }
                    >
                      Back to Login
                    </button>

                  </div>

                </form>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================
          MODAL CSS
      ===================================================== */}
      <style>{`

        .feedback-login-modal {
          z-index: 1055;
          background-color: rgba(
            0,
            0,
            0,
            0.5
          );
        }

        .feedback-login-modal
          .modal-dialog {
          max-width: 500px;
        }

        .feedback-login-modal
          .modal-content {
          border: none;
          border-radius: 6px;
          overflow: hidden;

          box-shadow:
            0 5px 20px
            rgba(
              0,
              0,
              0,
              0.25
            );
        }

        .feedback-modal-header {
          background-color: #e2520f;

          display: flex;
          align-items: center;
          justify-content: space-between;

          border-bottom: none;

          padding:
            1rem
            1.25rem;
        }

        .feedback-login-modal
          .modal-title {
          color: #ffffff;

          font-weight: 600;

          margin: 0;
        }

        /*
         * CLOSE X
         */
        .feedback-close-btn {
          background: transparent;

          border: none;

          color: #222222;

          font-size: 32px;

          font-weight: 300;

          line-height: 1;

          width: 38px;
          height: 38px;

          padding: 0;
          margin: 0;

          display: flex;

          align-items: center;
          justify-content: center;

          cursor: pointer;

          transition:
            opacity 0.2s ease,
            transform 0.2s ease;
        }

        .feedback-close-btn:hover {
          color: #000000;

          opacity: 0.7;

          transform: scale(1.05);
        }

        .feedback-close-btn:active {
          transform: scale(0.95);
        }

        .feedback-close-btn:focus {
          outline: none;
          box-shadow: none;
        }

        /*
         * BODY
         */
        .feedback-login-modal
          .modal-body {
          padding: 1.25rem;

          background-color: #ffffff;
        }

        /*
         * LABELS
         */
        .feedback-login-modal
          .form-label {
          font-weight: 500;

          margin-bottom: 0.4rem;
        }

        /*
         * INPUTS
         */
        .feedback-login-modal
          .form-control {
          min-height: 42px;

          border-radius: 4px;
        }

        .feedback-login-modal
          .form-control:focus {
          border-color: #e2520f;

          box-shadow:
            0 0 0 0.2rem
            rgba(
              226,
              82,
              15,
              0.15
            );
        }

        /*
         * PASSWORD INPUT
         */
        .feedback-login-modal
          .input-group
          .form-control {
          border-top-right-radius: 0;

          border-bottom-right-radius: 0;
        }

        /*
         * SHOW/HIDE BUTTON
         */
        .feedback-login-modal
          .input-group
          .btn {
          min-width: 70px;

          background-color: #6c757d;

          border-color: #6c757d;

          color: #ffffff;
        }

        .feedback-login-modal
          .input-group
          .btn:hover {
          background-color: #5c636a;

          border-color: #565e64;
        }

        /*
         * LOGIN / RESET BUTTON
         */
        .feedback-login-modal
          .ftthsa {
          min-height: 42px;

          border-radius: 4px;
        }

        /*
         * LINKS
         */
        .feedback-login-modal
          .btn-link {
          color: #e2520f;

          text-decoration: none;
        }

        .feedback-login-modal
          .btn-link:hover {
          color: #c7440b;

          text-decoration: underline;
        }

        /*
         * ALERT
         */
        .feedback-login-modal
          .alert {
          margin-bottom: 1rem;
        }

        /*
         * MOBILE
         */
        @media (max-width: 576px) {

          .feedback-login-modal
            .modal-dialog {
            margin: 0.75rem;
          }

          .feedback-login-modal
            .modal-body {
            padding: 1rem;
          }

          .feedback-modal-header {
            padding:
              0.85rem
              1rem;
          }

          .feedback-close-btn {
            font-size: 30px;

            width: 36px;
            height: 36px;
          }

        }

      `}</style>
    </>
  );
};

export default FeedbackLoginModal;